





































































































































import { Component, Emit, Prop, PropSync, Vue } from 'vue-property-decorator'

import {
  DxDataGrid,
  DxEditing,
  DxRemoteOperations,
  DxFilterRow,
  DxSelection,
  DxLoadPanel,
  DxColumn,
  DxGrouping,
  DxGroupPanel,
  DxPager,
  DxPaging,
  DxSearchPanel,
  DxMasterDetail,
  DxColumnChooser,
  DxColumnFixing,
  DxStateStoring,
  DxSorting,
  DxScrolling,
  DxRowDragging
} from 'devextreme-vue/data-grid'
import { DxColorBox } from 'devextreme-vue'
import { DxCheckBox } from 'devextreme-vue/check-box'
import DxTooltip from 'devextreme-vue/tooltip'
import { formatNumber, formatCurrency } from '@/utils/number-formatter'

interface CellPreparedEvent {
  column: {
    dataType?: string
    format?: string
  }
  value: any
  cellElement: HTMLElement
}

interface GridClickListenerEvent extends MouseEvent {
  target: EventTarget | null
}

@Component({
  name: 'TableGrid',
  components: {
    DxDataGrid,
    DxEditing,
    DxRemoteOperations,
    DxSelection,
    DxFilterRow,
    DxColumn,
    DxLoadPanel,
    DxGrouping,
    DxGroupPanel,
    DxPager,
    DxPaging,
    DxSearchPanel,
    DxMasterDetail,
    DxColumnChooser,
    DxColumnFixing,
    DxStateStoring,
    DxSorting,
    DxScrolling,
    DxColorBox,
    DxRowDragging,
    DxCheckBox,
    DxTooltip
  }
})
export default class TableGrid extends Vue {
  @Prop() public dataSource!: any
  @Prop({ default: undefined }) public keyExpr?: string | string[] | undefined
  @PropSync('checkedItems') public syncedCheckedItems!: Array<any>
  @Prop() public columns!: any
  @Prop() public summary!: any
  @Prop() public masterDetailEnable!: boolean
  @Prop({ default: 'single' }) public selectionMode!: string
  @Prop({ required: true }) private rowClick!: Function
  @Prop({ required: true }) private dblRowClick!: Function
  @Prop({ required: false }) private onChangeColor?: Function
  @Prop() public initNewRow!: Function
  @Prop({ default: 'calc(100vh - 200px)' }) public height!: number | string
  @Prop() public filterValue!: any
  @Prop() public onReorder!: any
  @Prop() public filterSyncEnabled!: boolean
  @Prop({ default: true }) public filterRowVisible!: boolean
  @Prop() public relationMode!: any
  @Prop({ default: true }) public columnChooserEnable!: boolean
  @Prop({ default: 'single' }) public sortingMode!: string
  @Prop({ default: 'push' }) public dropFeedbackMode!: string
  @Prop({ default: false }) public disabled!: boolean
  @Prop({ default: true }) public stateStoringEnable!: boolean
  @Prop({ default: false }) public autoExpandAll!: boolean
  @Prop({ default: false }) public allowReordering!: boolean
  @Prop({ default: false }) public showDragIcons!: boolean
  @Prop({ default: 'gridState_unknown_new' }) public stateStoringKey!: string
  @Prop() public contextMenuItems!: Array<any>
  @Prop() public groupingEnabled!: boolean
  @Prop() public buttons!: any
  @Prop() public toolbar!: any
  @Prop({ default: true }) public visible!: boolean
  @Prop({ default: true }) public showColumnHeaders!: boolean
  @Prop() public allowEditing!: boolean
  @Prop({ default: true }) public adding!: boolean
  @Prop({ default: true }) public updating!: boolean
  @Prop({ default: true }) public deleting!: boolean
  @Prop({ default: 'batch' }) public editorMode!: string
  @Prop({ default: false }) public editingUseIcons!: boolean
  @Prop({ default: true }) public confirmDelete!: boolean
  @Prop({ default: true }) public remoteFps!: boolean
  @Prop({ default: 10 }) public initialPageSize!: number
  @Prop({ default: true }) public enableLoadIndicator!: boolean
  @Prop({ default: true }) public enablePaginator!: boolean
  @Prop({ default: () => [10, 25, 50, 100] }) public pageSizes!: Array<number>
  @Prop({ default: true }) public enablePager!: boolean
  @Prop({ default: false }) public alwaysShowPager!: boolean
  @Prop({ default: 'id' }) public rowKeyField!: string
  @Prop({ default: true }) public rowAlternationEnabled!: boolean
  @Prop({ default: true }) public columnAutoWidth!: boolean
  @Prop({ default: true }) public wordWrapEnabled!: boolean
  @Prop({ default: '' }) public tooltipText!: string
  @Prop({ default: 'standard' }) public scrollMode!: 'standard' | 'infinite' | 'virtual'
  @Prop({ default: null }) public rowPrepared?: Function

  private focusedCell: HTMLElement | null = null
  private originalCellContent: string = ''
  private cellsCache: WeakMap<HTMLElement, { span: HTMLElement | null; initialized: boolean }> = new WeakMap()
  private handleDocumentClickBound: (event: MouseEvent) => void
  private handleGridClickBound: (event: MouseEvent) => void
  private documentClickListenerAttached: boolean = false
  private gridContainer: HTMLElement | null = null

  private clickedRow: boolean = false

  constructor(props: any) {
    super(props)
    this.handleDocumentClickBound = this.handleDocumentClick.bind(this)
    this.handleGridClickBound = this.handleGridClick.bind(this)
  }

  mounted(): void {
    this.attachGridClickListener()
    document.addEventListener('click', this.handleDocumentRowUnselect, true)
  }

  /**
   * Event delegation - один обработчик на всю таблицу вместо тысяч
   */
  private attachGridClickListener(): void {
    const gridRef = this.$refs.dxdatagrid as any
    if (gridRef && gridRef.$el) {
      this.gridContainer = gridRef.$el as HTMLElement
      this.gridContainer.removeEventListener('click', this.handleGridClickBound)
      this.gridContainer.addEventListener('click', this.handleGridClickBound)
    }
  }

  /**
   * Обработчик кликов на числовые ячейки через delegation
   */
  private handleGridClick(event: MouseEvent): void {
    const target = event.target as HTMLElement
    const cellElement = target.closest('[data-full-value]') as HTMLElement | null

    if (cellElement && cellElement instanceof HTMLElement) {
      this.handleNumberCellClick(event, cellElement)
    }
  }

  /**
   * Упрощенный обработчик checkbox клика
   */
  handleCheckboxClick(): void {
    // Vue автоматически обновит v-model через двусторонний binding
  }

  @Emit('content-ready')
  onContentReady(e: any): any {
    // Инициализируем слушатель после готовности контента
    this.attachGridClickListener()
    return e
  }

  @Emit('option-changed')
  private onOptionChanged(e: any): any {
    return e
  }

  /**
   * Оптимизированный onCellPrepared без множественных addEventListener
   */
  @Emit('cell-prepared')
  onCellPrepared(e: CellPreparedEvent): any {
    const { column, value, cellElement } = e

    // Обработка числовых ячеек
    if (
      column.dataType === 'number' ||
      column.format === 'number' ||
      column.format === 'currency'
    ) {
      cellElement.style.cursor = 'pointer'

      // Используем WeakMap для кэширования
      if (!this.cellsCache.has(cellElement)) {
        let span = cellElement.querySelector('span') as HTMLElement | null

        // Создаем span только если его нет
        if (!span) {
          const originalContent = cellElement.innerHTML
          span = document.createElement('span')
          span.innerHTML = originalContent
          cellElement.innerHTML = ''
          cellElement.appendChild(span)
        }

        // Кэшируем найденный span
        this.cellsCache.set(cellElement, { span, initialized: true })
      }

      const cacheData = this.cellsCache.get(cellElement)
      const span = cacheData?.span

      // Сохраняем данные в data-атрибуты
      cellElement.setAttribute('data-full-value', String(value))
      cellElement.setAttribute('data-column-format', column.format || 'number')

      // Форматируем только при изменении значения
      if (span && span.textContent !== String(value)) {
        if (column.format === 'currency') {
          span.textContent = formatCurrency(value)
        } else {
          span.textContent = formatNumber(value)
        }
      }
    }

    return e
  }
  private expandScientificNotation(value: string): string {
  const str = String(value).toLowerCase();

  if (!str.includes('e')) return str;

  const [mantissaRaw, expRaw] = str.split('e');
  const exponent = Number(expRaw);

  const negative = mantissaRaw.trim().startsWith('-');

  const pureMantissa = mantissaRaw.replace(/[+-]/g, '');

  const parts = pureMantissa.split('.');
  const intPart = parts[0] || '0';
  const fracPart = parts[1] || '';
  const digits = intPart + fracPart;
  const decimalPos = intPart.length;

  if (exponent === 0) {
    return (negative ? "-" : "") + pureMantissa;
  }

  const newPos = decimalPos + exponent;

  let result: string;

  if (newPos <= 0) {
    result = '0.' + '0'.repeat(-newPos) + digits;
  } else if (newPos >= digits.length) {
    result = digits + '0'.repeat(newPos - digits.length);
  } else {
    result = digits.slice(0, newPos) + '.' + digits.slice(newPos);
  }

  return negative ? '-' + result : result;
}



  private handleNumberCellClick(event: MouseEvent, cellElement: HTMLElement): void {
    event.stopPropagation()

    // Восстанавливаем предыдущую ячейку
    if (this.focusedCell && this.focusedCell !== cellElement) {
      this.restoreCellFormatting(this.focusedCell)
    }

    const fullValue = cellElement.getAttribute('data-full-value')
    const span = cellElement.querySelector('span') as HTMLElement | null

    if (fullValue && span) {
      this.originalCellContent = span.textContent || ''
      this.focusedCell = cellElement

      // Преобразуем научную нотацию в полное число
      let expandedValue = this.expandScientificNotation(fullValue)

      // Заменяем точку на запятую
      const formattedValue = expandedValue.replace('.', ',')
      span.textContent = formattedValue
      cellElement.classList.add('cell-focused')

      // Выделяем текст для копирования
      requestAnimationFrame(() => {
        const selection = window.getSelection()
        const range = document.createRange()
        if (span) {
          range.selectNodeContents(span)
          selection?.removeAllRanges()
          selection?.addRange(range)
        }
      })

      // Добавляем слушатель один раз
      if (!this.documentClickListenerAttached) {
        document.addEventListener('click', this.handleDocumentClickBound, true)
        this.documentClickListenerAttached = true
      }
    }
  }

  private handleDocumentClick = (event: MouseEvent): void => {
    if (this.focusedCell && !this.focusedCell.contains(event.target as Node)) {
      this.restoreCellFormatting(this.focusedCell)
      this.focusedCell = null
      document.removeEventListener('click', this.handleDocumentClickBound, true)
      this.documentClickListenerAttached = false
    }
  }

  private restoreCellFormatting(cellElement: HTMLElement): void {
    const span = cellElement.querySelector('span') as HTMLElement | null
    const fullValue = cellElement.getAttribute('data-full-value') || '0'
    const format = cellElement.getAttribute('data-column-format')

    if (span) {
      const numValue = parseFloat(fullValue)

      if (format === 'currency') {
        span.textContent = formatCurrency(numValue)
      } else {
        span.textContent = formatNumber(numValue)
      }
      cellElement.classList.remove('cell-focused')
    }
  }

  @Emit('row-prepared')
  onRowPrepared(e: any): any {
    return e
  }

  @Emit('row-expanded')
  onRowExpanded(e: any): any {
    return e
  }

  @Emit('row-collapsed')
  onRowCollapsed(e: any): any {
    return e
  }

  @Emit('row-updated')
  onRowUpdated(e: any): any {
    return e
  }

  @Emit('saving')
  onSaving(e: any): any {
    return e
  }

  @Emit('focused-cell-changed')
  onFocusedCellChanged(e: any): any {
    return e
  }

  @Emit('row-validating')
  onRowValidating(e: any): any {
    return e
  }

  @Emit('cell-click')
  onCellClick(e: any): any {
    return e
  }

  @Emit('init-new-row')
  onInitNewRow(e: any): any {
    return e
  }

  @Emit('edit-canceled')
  onEditCanceled(e: any): any {
    return e
  }

  editColor(e: any, cellInfo: any): void {
    if (this.onChangeColor) {
      this.onChangeColor(e.value, cellInfo.column.name, cellInfo.data)
    }
  }

  onRowClick(e: any): void {
    this.clickedRow = true

    if (e.isSelected) {
      this.rowClick(e)
    }

    // Разрешаем документу снова ловить клики
    requestAnimationFrame(() => {
      this.clickedRow = false
    })
  }

  private handleDocumentRowUnselect = (event: MouseEvent): void => {
    if (this.clickedRow) return

    const target = event.target as HTMLElement
    if (!target) return

    // если клик по числовой ячейке — не трогаем
    const numberCell = target.closest('[data-full-value]')
    if (numberCell) return

    // если клик внутри строки — тоже не трогаем
    const row = target.closest('.dx-data-row')
    if (row) return

    const grid = (this.$refs.dxdatagrid as any)?.instance
    if (!grid) return

    grid.clearSelection()
  }

  onRowDbkClick(e: any): void {
    if (e.rowType !== 'data') {
      return
    }
    this.dblRowClick(e)
  }

  onSelectionsChanged(e: any): void {
    this.$emit('update:syncedCheckedItems', this.syncedCheckedItems)
    this.emitSelectionChanged(e)
  }

  onGridRefresh(): void {
    this.dataSource.reload().catch((reason: any) => {
      // Обработка ошибки перезагрузки
    })
  }

  @Emit('selection-changed')
  emitSelectionChanged(e: any): any {
    return e
  }

  @Emit('row-removing')
  onRowRemoving(e: any): any {
    return e
  }

  @Emit('editor-preparing')
  onEditorPreparing(e: any): any {
    return e
  }

  addMenuItems(e: any): void {
    e.items = []

    if (e.row?.rowType === 'data' && this.contextMenuItems && Array.isArray(this.contextMenuItems)) {
      this.contextMenuItems.forEach((x) => {
        e.items.push({
          text: x.text,
          onItemClick: () => {
            x.action(e)
          }
        })
      })
    }
  }

  public refresh(): void {
    const grid = (this.$refs.dxdatagrid as any)?.instance
    if (grid) {
      grid.refresh()
    }
  }

  public repaint(): void {
    const grid = (this.$refs.dxdatagrid as any)?.instance
    if (grid) {
      grid.repaint()
    }
  }

  public navigateToRow(key: any): void {
    const grid = (this.$refs.dxdatagrid as any)?.instance
    if (grid) {
      grid.navigateToRow(key)
    }
  }

  public getDxGridInstance(): any {
    const grid = (this.$refs.dxdatagrid as any)?.instance
    return grid || null
  }

  beforeDestroy(): void {
    if (this.gridContainer) {
      this.gridContainer.removeEventListener('click', this.handleGridClickBound)
    }

    document.removeEventListener('click', this.handleDocumentRowUnselect, true)

    if (this.focusedCell) {
      this.restoreCellFormatting(this.focusedCell)
      this.focusedCell = null
    }

    if (this.documentClickListenerAttached) {
      document.removeEventListener('click', this.handleDocumentClickBound, true)
      this.documentClickListenerAttached = false
    }

    this.gridContainer = null
  }
}
